import { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Clock,
  Check,
  X,
  Edit3,
  Download,
  ChevronDown,
  ChevronRight,
  CalendarClock,
  AlertCircle,
} from "lucide-react";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.slice(11, 16);
  }
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};
const toIsoDateInput = (iso) => (iso || "").slice(0, 16); // for datetime-local

function statusBadge(status) {
  const cls = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
  }[status] || "bg-clinical-bg text-clinical-subtext border-clinical-border";
  return (
    <span
      className={`inline-flex items-center text-[10px] font-medium uppercase tracking-wide border ${cls} px-1.5 py-0.5 rounded`}
      data-testid={`tc-status-${status}`}
    >
      {status || "pending"}
    </span>
  );
}

export default function AdminTimecards() {
  const today = new Date();
  const [weekDate, setWeekDate] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // shift object being edited
  const [rejecting, setRejecting] = useState(null); // shift object being rejected

  const load = async (d = weekDate) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/timecards`, { params: { week: d } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load timecards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(weekDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDate]);

  const shiftWeek = (deltaDays) => {
    const d = new Date(weekDate);
    d.setDate(d.getDate() + deltaDays);
    setWeekDate(d.toISOString().slice(0, 10));
  };

  const totals = useMemo(() => {
    if (!data?.caregivers) return null;
    const t = { regular: 0, ot: 0, holiday: 0, mileage_amt: 0, gross: 0, pending: 0, approved: 0, rejected: 0 };
    for (const cg of data.caregivers) {
      t.regular += cg.totals.regular_hours;
      t.ot += cg.totals.overtime_hours;
      t.holiday += cg.totals.holiday_hours;
      t.mileage_amt += cg.totals.mileage_amount;
      t.gross += cg.totals.gross_amount;
      t.pending += cg.totals.pending_count || 0;
      t.approved += cg.totals.approved_count || 0;
      t.rejected += cg.totals.rejected_count || 0;
    }
    return t;
  }, [data]);

  const toggle = (cid) => setExpanded((e) => ({ ...e, [cid]: !e[cid] }));

  const approve = async (shift) => {
    try {
      await api.post(`/admin/timecards/${shift.id}/approve`);
      toast.success("Approved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    }
  };

  const bulkApprove = async (caregiver_id) => {
    if (!window.confirm(
      `Approve every PENDING shift this week${caregiver_id ? "" : " for all caregivers"}?`,
    )) return;
    try {
      const { data: res } = await api.post(`/admin/timecards/bulk-approve`, { week: weekDate, caregiver_id });
      toast.success(`Approved ${res.approved} shift${res.approved === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approve failed");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/timecards/${editing.id}`, {
        clock_in_at: editing.clock_in_at ? new Date(editing.clock_in_at).toISOString() : null,
        clock_out_at: editing.clock_out_at ? new Date(editing.clock_out_at).toISOString() : null,
        mileage_miles: editing.mileage_miles == null || editing.mileage_miles === "" ? 0 : Number(editing.mileage_miles),
        is_holiday: !!editing.is_holiday,
        admin_notes: editing.admin_notes || "",
      });
      toast.success("Saved (status reset to pending for re-approval)");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    if (!(rejecting.reason || "").trim()) {
      toast.error("A reason is required");
      return;
    }
    try {
      await api.post(`/admin/timecards/${rejecting.id}/reject`, { reason: rejecting.reason });
      toast.success("Rejected");
      setRejecting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  const exportCsv = (format) => {
    const url = `${API_BASE}/admin/timecards/export?week=${encodeURIComponent(weekDate)}&format=${format}`;
    // Same-origin cookie auth → simple anchor click works
    const a = document.createElement("a");
    a.href = url;
    a.click();
    toast.success(`${format.toUpperCase()} export downloading…`);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading timecards…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-timecards-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="label-eyebrow">Finance</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">Timecards & Payroll</h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Review and approve caregiver hours, then export to your payroll system.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftWeek(-7)}
            className="rounded-lg border border-clinical-border bg-white px-2.5 py-1.5 text-sm hover:bg-clinical-bg"
            data-testid="tc-prev-week"
          >
            ← Prev
          </button>
          <input
            type="date"
            value={weekDate}
            onChange={(e) => setWeekDate(e.target.value)}
            className="input-clinical text-sm w-44"
            data-testid="tc-week-input"
          />
          <button
            type="button"
            onClick={() => shiftWeek(7)}
            className="rounded-lg border border-clinical-border bg-white px-2.5 py-1.5 text-sm hover:bg-clinical-bg"
            data-testid="tc-next-week"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Top-line stats */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tc-week-stats">
          <Stat label="Regular hrs" value={(totals?.regular || 0).toFixed(2)} />
          <Stat label="Overtime hrs" value={(totals?.ot || 0).toFixed(2)} tone="overtime" />
          <Stat label="Holiday hrs" value={(totals?.holiday || 0).toFixed(2)} />
          <Stat label="Mileage" value={fmtMoney(totals?.mileage_amt)} />
          <Stat label="Gross payroll" value={fmtMoney(totals?.gross)} tone="primary" />
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="text-xs text-clinical-subtext">
          Week:{" "}
          <span className="font-medium text-clinical-text">
            {fmtDate(data?.week_start)} – {fmtDate(data?.week_end)}
          </span>
          {" · "}
          OT after{" "}
          <span className="font-medium text-clinical-text">
            {data?.payroll_settings?.weekly_overtime_threshold_h}h/wk
          </span>
          {" · "}
          mileage{" "}
          <span className="font-medium text-clinical-text">
            ${data?.payroll_settings?.mileage_rate_per_mile}/mi
          </span>
        </div>
        <div className="flex items-center gap-2">
          {totals?.pending > 0 && (
            <button
              type="button"
              onClick={() => bulkApprove(null)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-sm font-medium hover:bg-clinical-bg"
              data-testid="tc-bulk-approve"
            >
              <Check className="h-3.5 w-3.5" /> Approve all pending ({totals.pending})
            </button>
          )}
          <ExportMenu onExport={exportCsv} />
        </div>
      </div>

      {/* Caregiver list */}
      {(!data?.caregivers || data.caregivers.length === 0) ? (
        <div className="card-surface p-10 text-center" data-testid="tc-empty">
          <CalendarClock className="h-8 w-8 mx-auto text-clinical-subtext mb-2" />
          <h3 className="font-heading text-lg font-medium">No shifts this week yet</h3>
          <p className="mt-1 text-sm text-clinical-subtext">
            Once caregivers clock in and out, their hours will roll up here for approval.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.caregivers.map((cg) => (
            <CaregiverRow
              key={cg.caregiver_id}
              cg={cg}
              isOpen={!!expanded[cg.caregiver_id]}
              onToggle={() => toggle(cg.caregiver_id)}
              onApprove={approve}
              onBulkApprove={() => bulkApprove(cg.caregiver_id)}
              onEdit={(shift) => setEditing({ ...shift, clock_in_at: toIsoDateInput(shift.clock_in_at), clock_out_at: toIsoDateInput(shift.clock_out_at) })}
              onReject={(shift) => setRejecting({ ...shift, reason: "" })}
            />
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <Modal title="Edit shift" onClose={() => setEditing(null)} testid="tc-edit-modal">
          <div className="space-y-3 text-sm">
            <div className="text-xs text-clinical-subtext">
              {editing.caregiver_name} · {editing.client_name}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Clock in">
                <input
                  type="datetime-local"
                  className="input-clinical"
                  value={editing.clock_in_at}
                  onChange={(e) => setEditing({ ...editing, clock_in_at: e.target.value })}
                  data-testid="tc-edit-clock-in"
                />
              </Field>
              <Field label="Clock out">
                <input
                  type="datetime-local"
                  className="input-clinical"
                  value={editing.clock_out_at || ""}
                  onChange={(e) => setEditing({ ...editing, clock_out_at: e.target.value })}
                  data-testid="tc-edit-clock-out"
                />
              </Field>
              <Field label="Mileage (miles)">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className="input-clinical"
                  value={editing.mileage_miles ?? 0}
                  onChange={(e) => setEditing({ ...editing, mileage_miles: e.target.value })}
                  data-testid="tc-edit-mileage"
                />
              </Field>
              <Field label="Holiday rate">
                <label className="flex items-center gap-2 text-sm h-10">
                  <input
                    type="checkbox"
                    checked={!!editing.is_holiday}
                    onChange={(e) => setEditing({ ...editing, is_holiday: e.target.checked })}
                    data-testid="tc-edit-holiday"
                  />
                  <span>Apply holiday multiplier</span>
                </label>
              </Field>
            </div>
            <Field label="Notes (visible to caregiver after approval)">
              <textarea
                rows={2}
                className="input-clinical"
                value={editing.admin_notes || ""}
                onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })}
                data-testid="tc-edit-notes"
              />
            </Field>
            {editing.original_clock_in_at && (
              <div className="flex items-start gap-2 rounded bg-amber-50 border border-amber-200 p-2 text-xs">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />
                Originally: {fmtTime(editing.original_clock_in_at)} → {fmtTime(editing.original_clock_out_at)} (preserved for audit)
              </div>
            )}
            <p className="text-[11px] text-clinical-subtext">
              Saving will reset this shift to "pending" so you re-approve the change.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="text-sm text-clinical-subtext px-3 py-1.5">
                Cancel
              </button>
              <button onClick={saveEdit} className="btn-primary" data-testid="tc-edit-save">
                Save changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reject modal */}
      {rejecting && (
        <Modal title="Reject this shift?" onClose={() => setRejecting(null)} testid="tc-reject-modal">
          <div className="space-y-3 text-sm">
            <p className="text-clinical-subtext">
              Tell {rejecting.caregiver_name} why this shift isn't approved. They'll see the reason.
            </p>
            <textarea
              rows={3}
              className="input-clinical"
              placeholder="e.g. Clock-out time looks incorrect — please confirm…"
              value={rejecting.reason}
              onChange={(e) => setRejecting({ ...rejecting, reason: e.target.value })}
              data-testid="tc-reject-reason"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejecting(null)} className="text-sm text-clinical-subtext px-3 py-1.5">
                Cancel
              </button>
              <button
                onClick={submitReject}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-rose-700"
                data-testid="tc-reject-submit"
              >
                Reject shift
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = {
    primary: "text-clinical-primary",
    overtime: "text-amber-700",
  }[tone] || "text-clinical-text";
  return (
    <div className="card-surface p-3" data-testid={`tc-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 text-2xl font-heading font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

function CaregiverRow({ cg, isOpen, onToggle, onApprove, onBulkApprove, onEdit, onReject }) {
  const t = cg.totals;
  return (
    <div className="card-surface overflow-hidden" data-testid={`tc-caregiver-${cg.caregiver_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-clinical-bg/50"
        data-testid={`tc-toggle-${cg.caregiver_id}`}
      >
        {isOpen ? <ChevronDown className="h-4 w-4 text-clinical-subtext" /> : <ChevronRight className="h-4 w-4 text-clinical-subtext" />}
        <div className="flex-1">
          <div className="font-heading text-base font-medium">{cg.caregiver_name}</div>
          <div className="mt-0.5 text-xs text-clinical-subtext">
            ${cg.hourly_rate}/h · {cg.shifts.length} shift{cg.shifts.length === 1 ? "" : "s"}
            {t.pending_count > 0 && <span className="ml-2 text-amber-700">· {t.pending_count} pending</span>}
            {t.rejected_count > 0 && <span className="ml-2 text-rose-700">· {t.rejected_count} rejected</span>}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-5 text-xs text-right text-clinical-subtext">
          <div>
            <div className="label-eyebrow">Reg</div>
            <div className="text-clinical-text font-medium">{t.regular_hours.toFixed(2)}h</div>
          </div>
          <div>
            <div className="label-eyebrow text-amber-700">OT</div>
            <div className="text-amber-700 font-medium">{t.overtime_hours.toFixed(2)}h</div>
          </div>
          <div>
            <div className="label-eyebrow">Mileage</div>
            <div className="text-clinical-text font-medium">{fmtMoney(t.mileage_amount)}</div>
          </div>
          <div>
            <div className="label-eyebrow">Gross</div>
            <div className="text-clinical-primary font-semibold">{fmtMoney(t.gross_amount)}</div>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-clinical-border">
          {t.pending_count > 0 && (
            <div className="px-4 py-2 bg-amber-50/50 border-b border-amber-100 flex items-center justify-between text-xs">
              <span className="text-amber-800">{t.pending_count} shift{t.pending_count === 1 ? "" : "s"} waiting on your approval.</span>
              <button
                onClick={onBulkApprove}
                className="text-clinical-primary hover:underline font-medium"
                data-testid={`tc-cg-approve-all-${cg.caregiver_id}`}
              >
                Approve all
              </button>
            </div>
          )}
          <div className="divide-y divide-clinical-border">
            {cg.shifts.map((s) => (
              <ShiftRow
                key={s.id}
                shift={s}
                onApprove={() => onApprove(s)}
                onEdit={() => onEdit(s)}
                onReject={() => onReject(s)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ShiftRow({ shift, onApprove, onEdit, onReject }) {
  const dateLabel = fmtDate(shift.clock_in_at);
  const day = (() => {
    try { return WEEK_DAYS[new Date(shift.clock_in_at).getDay()]; } catch { return ""; }
  })();
  const wasEdited = !!shift.original_clock_in_at || !!shift.original_clock_out_at;
  return (
    <div className="p-4 flex flex-wrap items-center gap-3" data-testid={`tc-shift-${shift.id}`}>
      <div className="w-16 text-xs">
        <div className="font-medium text-clinical-text">{day}</div>
        <div className="text-clinical-subtext">{dateLabel.slice(dateLabel.indexOf(" ") + 1)}</div>
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-medium text-clinical-text">{shift.client_name}</div>
        <div className="text-xs text-clinical-subtext flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          {fmtTime(shift.clock_in_at)} → {shift.clock_out_at ? fmtTime(shift.clock_out_at) : "active"}
          {wasEdited && (
            <span className="ml-1 text-amber-700 inline-flex items-center gap-0.5" title="Edited by admin">
              <Edit3 className="h-3 w-3" /> edited
            </span>
          )}
          {shift.is_holiday && <span className="ml-1 text-fuchsia-700">· holiday</span>}
        </div>
        {shift.admin_notes && (
          <div className="mt-1 text-[11px] text-clinical-subtext italic">"{shift.admin_notes}"</div>
        )}
      </div>
      <div className="text-xs text-right w-16">
        <div className="label-eyebrow">Hours</div>
        <div className="font-medium">{Number(shift.hours || 0).toFixed(2)}</div>
      </div>
      <div className="text-xs text-right w-20">
        <div className="label-eyebrow">Amount</div>
        <div className="font-medium">{fmtMoney(shift.gross_amount)}</div>
      </div>
      <div>{statusBadge(shift.approval_status)}</div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="p-1.5 rounded hover:bg-clinical-bg text-clinical-subtext"
          title="Edit shift"
          data-testid={`tc-edit-${shift.id}`}
        >
          <Edit3 className="h-3.5 w-3.5" />
        </button>
        {shift.approval_status !== "approved" && shift.clock_out_at && (
          <button
            type="button"
            onClick={onApprove}
            className="inline-flex items-center gap-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 text-xs font-medium hover:bg-emerald-100"
            data-testid={`tc-approve-${shift.id}`}
          >
            <Check className="h-3 w-3" /> Approve
          </button>
        )}
        {shift.approval_status !== "rejected" && (
          <button
            type="button"
            onClick={onReject}
            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
            title="Reject"
            data-testid={`tc-reject-${shift.id}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ExportMenu({ onExport }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 btn-primary"
        data-testid="tc-export-button"
      >
        <Download className="h-3.5 w-3.5" /> Export
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 mt-1 w-56 rounded-lg border border-clinical-border bg-white shadow-lg z-50 overflow-hidden" data-testid="tc-export-menu" role="menu">
            {[
              { fmt: "csv", label: "Detailed CSV", hint: "All shifts, full breakdown" },
              { fmt: "quickbooks", label: "QuickBooks CSV", hint: "Daily aggregates" },
              { fmt: "gusto", label: "Gusto CSV", hint: "Weekly per-employee" },
            ].map((opt) => (
              <button
                key={opt.fmt}
                onClick={() => { onExport(opt.fmt); setOpen(false); }}
                className="block w-full text-left px-3 py-2 hover:bg-clinical-bg"
                data-testid={`tc-export-${opt.fmt}`}
                role="menuitem"
              >
                <div className="text-sm font-medium text-clinical-text">{opt.label}</div>
                <div className="text-[11px] text-clinical-subtext">{opt.hint}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white border border-clinical-border shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-medium">{title}</h2>
          <button onClick={onClose} className="text-clinical-subtext hover:text-clinical-text">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
